import type { ExerciseTemplate } from '@yuan-math/exercises';
import { roundTo } from '@yuan-math/math';
import { choiceExercise, decimalTolerance, numericExercise, options, template, trap } from './authoring.ts';

/** Perfect squares up to 400, which is the range these exercises stay inside. */
const SQUARES = [1, 4, 9, 16, 25, 36, 49, 64, 81, 100, 121, 144, 169, 196, 225, 256, 289, 324, 361, 400];

/** Perfect cubes up to 1728, the range the cube-root exercises stay inside. */
const CUBES = [1, 8, 27, 64, 125, 216, 343, 512, 729, 1000, 1331, 1728];

/**
 * Leg pairs whose hypotenuse is irrational — every pair from 2 to 7 except the
 * 3-4-5. Listed rather than filtered at build time, because a template must
 * always return an exercise and cannot reject the parameters it drew.
 */
const IRRATIONAL_LEG_PAIRS: readonly (readonly [number, number])[] = [
  [2, 2], [2, 3], [2, 4], [2, 5], [2, 6], [2, 7],
  [3, 3], [3, 5], [3, 6], [3, 7],
  [4, 4], [4, 5], [4, 6], [4, 7],
  [5, 5], [5, 6], [5, 7],
  [6, 6], [6, 7],
  [7, 7],
];

/** Square roots, cube roots, irrationality, and the theorem that needs them. */
export const ROOT_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'square-root-perfect',
    conceptId: 'square-roots',
    description: 'Find an exact square root.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const root = rng.int(2, difficulty === 2 ? 10 : 20);
      const square = root * root;

      return numericExercise({
        prompt: 'Work this out.',
        latex: `\\sqrt{${square}}`,
        value: root,
        summary: `${root} × ${root} = ${square}, so the square root of ${square} is ${root}.`,
        hint: 'What number multiplied by itself gives this?',
        steps: [
          { label: 'Look for the number that squares to it.', latex: `${root}^2 = ${square}` },
          { label: 'So the root is that number.', latex: `\\sqrt{${square}} = ${root}` },
        ],
        // Halving instead of rooting — and it must not accidentally be right.
        traps: [...(square / 2 === root ? [] : [trap(square / 2, 'root-halved')])],
      });
    },
  }),

  template({
    id: 'square-root-estimate',
    conceptId: 'square-roots',
    description: 'Bound an irrational root between two whole numbers.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const lower = rng.int(2, 12);
      const square = lower * lower + rng.int(1, 2 * lower);

      return numericExercise({
        prompt: `√${square} is not a whole number. Between which two consecutive whole numbers does it lie? Give the smaller one.`,
        latex: `\\sqrt{${square}}`,
        value: lower,
        summary: `${lower}² = ${lower * lower} and ${lower + 1}² = ${(lower + 1) ** 2}, and ${square} sits between them, so √${square} is between ${lower} and ${lower + 1}.`,
        hint: 'Find the perfect squares just below and just above this number.',
        steps: [
          { label: 'The perfect square below.', latex: `${lower}^2 = ${lower * lower}` },
          { label: 'The perfect square above.', latex: `${lower + 1}^2 = ${(lower + 1) ** 2}` },
          { label: 'So the root is between them.', latex: `${lower} < \\sqrt{${square}} < ${lower + 1}` },
        ],
        traps: [...(square / 2 === lower ? [] : [trap(roundTo(square / 2, 2), 'root-halved')])],
      });
    },
  }),

  template({
    id: 'square-root-of-sum',
    conceptId: 'square-roots',
    description: 'Take a root of a sum, where distributing is tempting and wrong.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      // A Pythagorean triple, so the root is exact and the wrong answer is the
      // classic one: rooting each term and adding.
      const triple = rng.pick([
        { a: 3, b: 4, c: 5 },
        { a: 6, b: 8, c: 10 },
        { a: 5, b: 12, c: 13 },
        { a: 8, b: 15, c: 17 },
        { a: 9, b: 12, c: 15 },
      ]);
      const sum = triple.a ** 2 + triple.b ** 2;

      return numericExercise({
        prompt: 'Work this out.',
        latex: `\\sqrt{${triple.a}^2 + ${triple.b}^2}`,
        value: triple.c,
        summary: `Add first: ${triple.a ** 2} + ${triple.b ** 2} = ${sum}, and √${sum} = ${triple.c}.`,
        hint: 'The root cannot be taken of each piece separately. What is under the root sign altogether?',
        steps: [
          { label: 'Square each number.', latex: `${triple.a ** 2} + ${triple.b ** 2}` },
          { label: 'Add them.', latex: `${sum}` },
          { label: 'Now take the root.', latex: `\\sqrt{${sum}} = ${triple.c}` },
        ],
        traps: [trap(triple.a + triple.b, 'root-distributed-over-sum')],
      });
    },
  }),

  template({
    id: 'rational-or-irrational',
    conceptId: 'square-roots',
    description: 'Decide which of four numbers is irrational.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const perfect = rng.pick(SQUARES.slice(2));
      const notPerfect = rng.pick([2, 3, 5, 6, 7, 8, 10, 11, 12, 13, 15].filter((n) => !SQUARES.includes(n)));
      const whole = rng.int(2, 20);
      const denominator = rng.int(2, 9);

      const { choices, correctId } = options(
        [
          { latex: `\\sqrt{${notPerfect}}`, correct: true },
          { latex: `\\sqrt{${perfect}}`, misconceptionId: 'root-halved' },
          { latex: `\\frac{${whole}}{${denominator}}`, misconceptionId: 'root-halved' },
          { latex: `${roundTo(whole + 0.25, 2)}`, misconceptionId: 'root-halved' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which of these is irrational — that is, which cannot be written as one whole number over another?',
        choices,
        correctId,
        summary: `√${perfect} is exactly ${Math.sqrt(perfect)}, and a fraction or a terminating decimal is rational by definition. √${notPerfect} is not a perfect square, so its decimal never ends and never repeats.`,
        hint: 'A root is only irrational when the number under it is not a perfect square.',
      });
    },
  }),

  template({
    id: 'square-root-area-to-side',
    conceptId: 'square-roots',
    description: 'Recover the side of a square from its area — the question a root was invented for.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const side = rng.int(4, difficulty === 2 ? 12 : 20);
      const area = side * side;

      return numericExercise({
        prompt: `A square tile has an area of ${area} square cm. How long is one side?`,
        unit: 'cm',
        value: side,
        summary: `A square's area is a side times itself, and ${side} × ${side} = ${area}, so each side is ${side} cm.`,
        hint: 'The area of a square is one side multiplied by itself. Which operation undoes that?',
        steps: [
          { label: 'Both sides of a square are equal, so the area is s².', latex: `s^2 = ${area}` },
          { label: 'Take the square root of both sides.', latex: `s = \\sqrt{${area}} = ${side}` },
        ],
        traps: [...(area / 2 === side ? [] : [trap(roundTo(area / 2, 2), 'root-halved')])],
      });
    },
  }),

  /* ------------------------------------------------------------ cube roots */
  template({
    id: 'cube-root-perfect',
    conceptId: 'cube-roots',
    description: 'Find an exact cube root.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const root = rng.int(2, difficulty === 2 ? 7 : 12);
      const cube = root ** 3;

      return numericExercise({
        prompt: 'Work this out.',
        latex: `\\sqrt[3]{${cube}}`,
        value: root,
        summary: `${root} × ${root} × ${root} = ${cube}, so the cube root of ${cube} is ${root}.`,
        hint: 'What number, multiplied by itself three times over, gives this?',
        steps: [
          { label: 'Look for the number that cubes to it.', latex: `${root}^3 = ${cube}` },
          { label: 'So the cube root is that number.', latex: `\\sqrt[3]{${cube}} = ${root}` },
        ],
        traps: [
          trap(roundTo(cube / 3, 2), 'cube-root-as-third'),
          ...(roundTo(Math.sqrt(cube), 2) === roundTo(cube / 3, 2) ? [] : [trap(roundTo(Math.sqrt(cube), 2), 'root-index-ignored')]),
        ],
      });
    },
  }),

  template({
    id: 'cube-root-edge-from-volume',
    conceptId: 'cube-roots',
    description: 'Recover the edge of a cube from its volume.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const edge = rng.int(2, difficulty === 3 ? 8 : 12);
      const volume = edge ** 3;

      return numericExercise({
        prompt: `A cubical box has a volume of ${volume} cubic cm. How long is each edge?`,
        unit: 'cm',
        figure: { kind: 'prism', length: 3, width: 3, height: 3, labels: { length: '?', width: '?', height: '?' } },
        value: edge,
        summary: `All three edges of a cube are equal, so e³ = ${volume}, and ${edge} × ${edge} × ${edge} = ${volume}.`,
        hint: 'A cube has three equal edges multiplied together. Undo three factors, not two.',
        steps: [
          { label: 'The volume of a cube is e³.', latex: `e^3 = ${volume}` },
          { label: 'Take the cube root of both sides.', latex: `e = \\sqrt[3]{${volume}} = ${edge}` },
        ],
        traps: [
          trap(roundTo(Math.sqrt(volume), 2), 'root-index-ignored'),
          ...(roundTo(volume / 3, 2) === roundTo(Math.sqrt(volume), 2) ? [] : [trap(roundTo(volume / 3, 2), 'cube-root-as-third')]),
        ],
      });
    },
  }),

  template({
    id: 'cube-root-estimate',
    conceptId: 'cube-roots',
    description: 'Bound a cube root between two whole numbers, where the square root is a tempting wrong landmark.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const lower = rng.int(2, 9);
      const gap = CUBES[lower]! - CUBES[lower - 1]!;
      const value = lower ** 3 + rng.int(1, gap - 1);
      const nearestSquare = Math.floor(Math.sqrt(value));

      return numericExercise({
        prompt: `∛${value} is not a whole number. Between which two consecutive whole numbers does it lie? Give the smaller one.`,
        latex: `\\sqrt[3]{${value}}`,
        value: lower,
        summary: `${lower}³ = ${lower ** 3} and ${lower + 1}³ = ${(lower + 1) ** 3}, and ${value} sits between them, so ∛${value} is between ${lower} and ${lower + 1}.`,
        hint: 'Find the perfect cubes just below and just above this number — there are far fewer of them than squares.',
        steps: [
          { label: 'The perfect cube below.', latex: `${lower}^3 = ${lower ** 3}` },
          { label: 'The perfect cube above.', latex: `${lower + 1}^3 = ${(lower + 1) ** 3}` },
          { label: 'So the root is between them.', latex: `${lower} < \\sqrt[3]{${value}} < ${lower + 1}` },
        ],
        /*
         * The square-root landmark is the diagnostic one here, so it is kept
         * and the division is dropped when the two coincide — ∛9 puts both at
         * 3, and one wrong answer must never carry two diagnoses.
         */
        traps: [
          trap(nearestSquare, 'root-index-ignored'),
          ...(roundTo(value / 3, 2) === nearestSquare || roundTo(value / 3, 2) === lower
            ? []
            : [trap(roundTo(value / 3, 2), 'cube-root-as-third')]),
        ],
      });
    },
  }),

  /* ------------------------------------------------ the Pythagorean theorem */
  template({
    id: 'pythagoras-hypotenuse',
    conceptId: 'pythagoras',
    description: 'Find the hypotenuse of a right triangle.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const triple = rng.pick([
        { a: 3, b: 4, c: 5 },
        { a: 6, b: 8, c: 10 },
        { a: 5, b: 12, c: 13 },
        { a: 9, b: 12, c: 15 },
        { a: 8, b: 15, c: 17 },
        { a: 7, b: 24, c: 25 },
        { a: 20, b: 21, c: 29 },
      ]);

      return numericExercise({
        prompt: `A right triangle has shorter sides of ${triple.a} cm and ${triple.b} cm. How long is the hypotenuse?`,
        unit: 'cm',
        figure: {
          kind: 'shape',
          shape: 'triangle',
          labels: { base: `${triple.b} cm`, height: `${triple.a} cm`, hypotenuse: '?' },
        },
        value: triple.c,
        summary: `${triple.a}² + ${triple.b}² = ${triple.a ** 2} + ${triple.b ** 2} = ${triple.c ** 2}, and √${triple.c ** 2} = ${triple.c} cm.`,
        hint: 'Square both short sides, add them, then take the square root.',
        steps: [
          { label: 'Square the two shorter sides.', latex: `${triple.a}^2 + ${triple.b}^2 = ${triple.a ** 2} + ${triple.b ** 2}` },
          { label: 'Add.', latex: `c^2 = ${triple.c ** 2}` },
          { label: 'Take the square root.', latex: `c = ${triple.c}` },
        ],
        traps: [
          trap(triple.a + triple.b, 'root-distributed-over-sum'),
          trap(triple.c ** 2, 'root-halved'),
        ],
      });
    },
  }),

  template({
    id: 'pythagoras-leg',
    conceptId: 'pythagoras',
    description: 'Find a shorter side, where subtracting rather than adding is the whole point.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const triple = rng.pick([
        { a: 3, b: 4, c: 5 },
        { a: 6, b: 8, c: 10 },
        { a: 5, b: 12, c: 13 },
        { a: 9, b: 12, c: 15 },
        { a: 8, b: 15, c: 17 },
        { a: 7, b: 24, c: 25 },
      ]);

      return numericExercise({
        prompt: `A right triangle has a hypotenuse of ${triple.c} cm and one shorter side of ${triple.b} cm. How long is the other side?`,
        unit: 'cm',
        figure: {
          kind: 'shape',
          shape: 'triangle',
          labels: { base: `${triple.b} cm`, height: '?', hypotenuse: `${triple.c} cm` },
        },
        value: triple.a,
        summary: `Rearranged, a² = c² − b² = ${triple.c ** 2} − ${triple.b ** 2} = ${triple.a ** 2}, so a = ${triple.a} cm.`,
        hint: 'The hypotenuse is the longest side, and you were given it. Does the missing side make the total bigger or smaller?',
        steps: [
          { label: 'Start from the theorem.', latex: `a^2 + ${triple.b}^2 = ${triple.c}^2` },
          { label: 'Rearrange to isolate the unknown.', latex: `a^2 = ${triple.c ** 2} - ${triple.b ** 2} = ${triple.a ** 2}` },
          { label: 'Take the square root.', latex: `a = ${triple.a}` },
        ],
        traps: [
          // Adding the two given sides, as though solving for the hypotenuse.
          trap(roundTo(Math.sqrt(triple.c ** 2 + triple.b ** 2), 2), 'pythagoras-leg-added'),
          trap(triple.c - triple.b, 'root-distributed-over-sum'),
        ],
      });
    },
  }),

  template({
    id: 'pythagoras-is-right-angled',
    conceptId: 'pythagoras',
    description: 'Use the converse to test whether a triangle has a right angle.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const right = rng.bool();
      const triple = rng.pick([
        { a: 3, b: 4, c: 5 },
        { a: 5, b: 12, c: 13 },
        { a: 8, b: 15, c: 17 },
        { a: 6, b: 8, c: 10 },
      ]);
      const c = right ? triple.c : triple.c + rng.int(1, 2);
      const sumOfSquares = triple.a ** 2 + triple.b ** 2;

      const { choices, correctId } = options(
        [
          {
            label: right ? 'Yes — the theorem holds exactly' : 'No — the numbers do not satisfy the theorem',
            correct: true,
          },
          {
            label: right ? 'No — the numbers do not satisfy the theorem' : 'Yes — the theorem holds exactly',
            misconceptionId: 'pythagoras-leg-added',
          },
          { label: 'It cannot be decided without measuring the angle', misconceptionId: 'pythagoras-leg-added' },
          { label: 'Only if the sides are whole numbers', misconceptionId: 'pythagoras-leg-added' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `A triangle has sides of ${triple.a}, ${triple.b} and ${c}. Is it right-angled?`,
        choices,
        correctId,
        summary: `${triple.a}² + ${triple.b}² = ${sumOfSquares}, and ${c}² = ${c ** 2}. They ${right ? 'match, so the triangle is right-angled' : 'do not match, so it is not'}.`,
        hint: 'Square the two shorter sides and add. Does it come to the square of the longest?',
        steps: [
          { label: 'Square the two shorter sides and add.', latex: `${triple.a}^2 + ${triple.b}^2 = ${sumOfSquares}` },
          { label: 'Square the longest side.', latex: `${c}^2 = ${c ** 2}` },
          { label: right ? 'They are equal.' : 'They are not equal.', latex: `${sumOfSquares} ${right ? '=' : '\\ne'} ${c ** 2}` },
        ],
      });
    },
  }),

  template({
    id: 'coordinate-distance-between-points',
    conceptId: 'coordinate-distance',
    description: 'Find the distance between two points, which is the theorem wearing a coordinate grid.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const triple = rng.pick([
        { a: 3, b: 4, c: 5 },
        { a: 4, b: 3, c: 5 },
        { a: 6, b: 8, c: 10 },
        { a: 5, b: 12, c: 13 },
        { a: 12, b: 5, c: 13 },
        { a: 9, b: 12, c: 15 },
      ]);
      const x1 = rng.int(-6, 6 - triple.b);
      const y1 = rng.int(-6, 6 - triple.a);
      const x2 = x1 + triple.b;
      const y2 = y1 + triple.a;

      return numericExercise({
        prompt: `How far apart are the points (${x1}, ${y1}) and (${x2}, ${y2})?`,
        unit: 'units',
        figure: {
          kind: 'coordinate-grid',
          min: -8,
          max: 8,
          points: [
            { x: x1, y: y1, label: `(${x1}, ${y1})` },
            { x: x2, y: y2, label: `(${x2}, ${y2})` },
          ],
        },
        value: triple.c,
        summary: `The two points differ by ${triple.b} across and ${triple.a} up, and those are the legs of a right triangle: √(${triple.b}² + ${triple.a}²) = √${triple.c ** 2} = ${triple.c}.`,
        hint: 'Draw the horizontal and vertical steps between the points. They make a right triangle whose hypotenuse is the distance.',
        steps: [
          { label: 'Find the horizontal and vertical differences.', latex: `${triple.b} \\text{ across},\\quad ${triple.a} \\text{ up}` },
          { label: 'Square each and add.', latex: `${triple.b ** 2} + ${triple.a ** 2} = ${triple.c ** 2}` },
          { label: 'Take the square root.', latex: `d = ${triple.c}` },
        ],
        traps: [
          trap(triple.a + triple.b, 'root-distributed-over-sum'),
          trap(triple.c ** 2, 'sum-of-squares-not-rooted'),
        ],
      });
    },
  }),

  template({
    id: 'coordinate-distance-leg',
    conceptId: 'coordinate-distance',
    description: 'Find one leg of the right triangle two points imply.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const x1 = rng.int(-7, 7);
      const y1 = rng.int(-7, 7);
      let dx = rng.int(2, 9) * (rng.bool() ? 1 : -1);
      let dy = rng.int(2, 9) * (rng.bool() ? 1 : -1);
      // Equal legs would make the two readings indistinguishable, and the trap
      // below would grade as the right answer.
      if (Math.abs(dx) === Math.abs(dy)) dy = dy > 0 ? dy + 1 : dy - 1;
      const x2 = x1 + dx;
      const y2 = y1 + dy;
      const wantHorizontal = rng.bool();
      const answer = Math.abs(wantHorizontal ? dx : dy);
      const other = Math.abs(wantHorizontal ? dy : dx);

      return numericExercise({
        prompt: `A right triangle is drawn with (${x1}, ${y1}) and (${x2}, ${y2}) at the ends of its longest side. How long is its ${wantHorizontal ? 'horizontal' : 'vertical'} leg?`,
        unit: 'units',
        figure: {
          kind: 'coordinate-grid',
          min: -10,
          max: 10,
          points: [
            { x: x1, y: y1, label: `(${x1}, ${y1})` },
            { x: x2, y: y1 },
            { x: x2, y: y2, label: `(${x2}, ${y2})` },
          ],
        },
        value: answer,
        summary: `The ${wantHorizontal ? 'horizontal' : 'vertical'} leg is the difference of the ${wantHorizontal ? 'x' : 'y'}-coordinates, taken as a length: |${wantHorizontal ? `${x2} - (${x1})` : `${y2} - (${y1})`}| = ${answer}.`,
        hint: 'The horizontal leg only depends on the x-coordinates, and the vertical leg only on the y-coordinates. A length is never negative.',
        steps: [
          {
            label: `Subtract the ${wantHorizontal ? 'x' : 'y'}-coordinates.`,
            latex: wantHorizontal ? `${x2} - (${x1}) = ${dx}` : `${y2} - (${y1}) = ${dy}`,
          },
          { label: 'Take it as a length.', latex: `${answer}` },
        ],
        traps: [trap(other, 'coordinates-reversed')],
      });
    },
  }),

  template({
    id: 'coordinate-distance-irrational',
    conceptId: 'coordinate-distance',
    description: 'A distance that is not a whole number, wanted to one decimal place.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const x1 = rng.int(-6, 2);
      const y1 = rng.int(-6, 2);
      const [first, second] = rng.pick(IRRATIONAL_LEG_PAIRS) as readonly [number, number];
      const dx = rng.bool() ? first : second;
      const dy = dx === first ? second : first;
      const squared = dx * dx + dy * dy;
      const answer = roundTo(Math.sqrt(squared), 1);

      return numericExercise({
        prompt: `How far apart are the points (${x1}, ${y1}) and (${x1 + dx}, ${y1 + dy})? Give your answer to one decimal place.`,
        unit: 'units',
        figure: {
          kind: 'coordinate-grid',
          min: -8,
          max: 8,
          points: [
            { x: x1, y: y1, label: `(${x1}, ${y1})` },
            { x: x1 + dx, y: y1 + dy, label: `(${x1 + dx}, ${y1 + dy})` },
          ],
        },
        value: answer,
        tolerance: decimalTolerance(answer, 1),
        summary: `The legs are ${dx} and ${dy}, so d = √(${dx * dx} + ${dy * dy}) = √${squared} ≈ ${answer}. There is no whole number whose square is ${squared}, so the exact distance cannot be written down at all.`,
        hint: 'Square both differences and add them. The answer is the square root of that total, not the total itself.',
        steps: [
          { label: 'Square each leg.', latex: `${dx}^{2} = ${dx * dx},\\quad ${dy}^{2} = ${dy * dy}` },
          { label: 'Add them.', latex: `${dx * dx} + ${dy * dy} = ${squared}` },
          { label: 'Take the square root.', latex: `\\sqrt{${squared}} \\approx ${answer}` },
        ],
        traps: [trap(squared, 'distance-not-rooted'), trap(dx + dy, 'pythagoras-leg-added')],
      });
    },
  }),

  template({
    id: 'coordinate-distance-method',
    conceptId: 'coordinate-distance',
    description: 'Choose the calculation that gives the distance between two points.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const dx = rng.int(3, 9);
      const dy = rng.int(2, 9);
      const x1 = rng.int(-6, 0);
      const y1 = rng.int(-6, 0);

      const { choices, correctId } = options(
        [
          { latex: `\\sqrt{${dx}^{2} + ${dy}^{2}}`, correct: true },
          { latex: `${dx} + ${dy}`, misconceptionId: 'pythagoras-leg-added' },
          { latex: `${dx}^{2} + ${dy}^{2}`, misconceptionId: 'distance-not-rooted' },
          { latex: `\\sqrt{${dx}} + \\sqrt{${dy}}`, misconceptionId: 'root-distributed-over-sum' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `The points (${x1}, ${y1}) and (${x1 + dx}, ${y1 + dy}) are ${dx} apart across and ${dy} apart up. Which calculation gives the distance between them?`,
        choices,
        correctId,
        summary:
          'The two steps are the legs of a right triangle and the distance is its hypotenuse, so both legs are squared, added, and the total rooted. Adding the legs gives the journey along the grid lines, which is longer.',
        hint: 'The direct route is the hypotenuse. Which of these is a² + b² = c² rearranged to give c?',
      });
    },
  }),
];
